/**
 * Protected Route component for role-based access control.
 * 
 * Wraps routes that require specific permissions or roles,
 * redirecting unauthorized users to an access denied page.
 * 
 * **Requirements: 14.3**
 */
import React from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Typography, Paper, Button } from '@mui/material'
import { Lock as LockIcon } from '@mui/icons-material'
import { usePermissions } from '../hooks/usePermissions'

interface ProtectedRouteProps {
  children: React.ReactNode
  requirePermission?: string
  requireAnyPermission?: string[]
  requireAllPermissions?: string[]
  requireRole?: string
  requireConfigurationAccess?: boolean
}

/**
 * Access Denied component shown when user lacks required permissions
 */
const AccessDenied: React.FC<{ requiredPermission?: string }> = ({ requiredPermission }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          textAlign: 'center'
        }}
      >
        <LockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          You do not have permission to access this page.
        </Typography>
        {requiredPermission && (
          <Typography variant="body2" color="text.secondary" paragraph>
            Required: {requiredPermission}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={() => window.history.back()}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Paper>
    </Box>
  )
}

/**
 * Protected Route wrapper component
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePermission,
  requireAnyPermission,
  requireAllPermissions,
  requireRole,
  requireConfigurationAccess
}) => {
  const {
    user,
    hasPermission,
    hasAnyPermission,
    hasRole,
    canManageConfigurations,
    isSuperuser
  } = usePermissions()

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Superuser has access to everything
  if (isSuperuser()) {
    return <>{children}</>
  }

  // Check configuration access requirement
  if (requireConfigurationAccess && !canManageConfigurations()) {
    return <AccessDenied requiredPermission="Configuration Management (system_admin or integration_admin role)" />
  }

  // Check specific role requirement
  if (requireRole && !hasRole(requireRole)) {
    return <AccessDenied requiredPermission={`Role: ${requireRole}`} />
  }

  // Check single permission requirement
  if (requirePermission && !hasPermission(requirePermission)) {
    return <AccessDenied requiredPermission={requirePermission} />
  }

  // Check any permission requirement (user needs at least one)
  if (requireAnyPermission && !hasAnyPermission(requireAnyPermission)) {
    return <AccessDenied requiredPermission={`One of: ${requireAnyPermission.join(', ')}`} />
  }

  // Check all permissions requirement (user needs all)
  if (requireAllPermissions) {
    const hasAll = requireAllPermissions.every(perm => hasPermission(perm))
    if (!hasAll) {
      return <AccessDenied requiredPermission={`All of: ${requireAllPermissions.join(', ')}`} />
    }
  }

  // User has required permissions, render children
  return <>{children}</>
}

export default ProtectedRoute
